/**
 * A selectable category facet for the University Finder. `value` is the full tag name the API returns on
 * UniversityResult.tags (so client-side filtering matches against it); `label` is the UI text.
 */
export interface UniversityFacetOption {
  label: string;
  value: string;
}

/**
 * Degrees facets (College::Degrees::*) — AND logic (a university must offer ALL selected), mirroring the iOS
 * app's empty `orLogicCategories` for colleges.
 */
export const UNIVERSITY_DEGREE_OPTIONS: UniversityFacetOption[] = [
  { label: 'Associate', value: 'College::Degrees::Associates' },
  { label: 'Bachelor\'s', value: 'College::Degrees::Bachelors' },
  { label: 'Master\'s', value: 'College::Degrees::Masters' },
  { label: 'Doctorate', value: 'College::Degrees::Doctorate' }
];

/**
 * Majors facets (College::Majors::*) — AND logic.
 */
export const UNIVERSITY_MAJOR_OPTIONS: UniversityFacetOption[] = [
  { label: 'Aerospace Engineering', value: 'College::Majors::Aerospace Engineering' },
  { label: 'Air Traffic Control', value: 'College::Majors::ATC' },
  { label: 'Aircraft Dispatch', value: 'College::Majors::Aircraft Dispatch' },
  { label: 'Aircraft Maintenance', value: 'College::Majors::Aircraft Maintenance' },
  { label: 'Airport Management', value: 'College::Majors::Airport Management' },
  { label: 'Aviation Business Administration', value: 'College::Majors::Aviation Business Administration' },
  { label: 'Aviation Management', value: 'College::Majors::Aviation Management' },
  { label: 'Maintenance Management', value: 'College::Majors::Maintenance Management' },
  { label: 'Professional Pilot', value: 'College::Majors::Professional Pilot' },
  { label: 'Unmanned Aerial Systems', value: 'College::Majors::Unmanned Aerial Systems' }
];

/**
 * Accreditation facets (College::Accreditation::*) — AND logic.
 */
export const UNIVERSITY_ACCREDITATION_OPTIONS: UniversityFacetOption[] = [
  { label: 'Aviation Accreditation Board International (AABI)', value: 'College::Accreditation::Aviation Accreditation Board International (AABI)' },
  { label: 'University Aviation Association (UAA)', value: 'College::Accreditation::University Aviation Association (UAA)' }
];

/**
 * Additional (two-segment) College facets — AND logic.
 */
export const UNIVERSITY_OPTION_OPTIONS: UniversityFacetOption[] = [
  { label: 'Approved for VA', value: 'College::Approved for VA' },
  { label: 'Distance Learning', value: 'College::Distance Learning' }
];

/**
 * Filter state for the University Finder. Holds the keyword plus the selected category facets. Selected facets
 * are stored as arrays of tag names so the AND filtering logic (see SearchPageComponent.filterUniversities)
 * can match directly against UniversityResult.tags. Empty arrays mean "no constraint" (show all). Mirrors
 * {@link FlyingClubFinderFilter}.
 */
export class UniversityFinderFilter {

  /** Keyword: university name or airport ident. */
  name = '';

  /** Selected Degrees tag names (AND logic). */
  degrees: string[] = [];

  /** Selected Majors tag names (AND logic). */
  majors: string[] = [];

  /** Selected Accreditation tag names (AND logic). */
  accreditation: string[] = [];

  /** Selected additional (VA Approved / Distance Learning) tag names (AND logic). */
  options: string[] = [];
}
